import React, { useState } from 'react';
import { Navigation } from '../App';
import { Shuffle, TrendingUp, AlertCircle, CheckCircle, Target } from 'lucide-react';

export default function PracticePage() {
  const [selectedHand, setSelectedHand] = useState(0);

  const practiceHands = [
    {
      id: 1,
      title: "Pocket Aces - Premium Starting Hand",
      difficulty: "Beginner",
      position: "Button",
      situation: "Pre-Flop",
      yourCards: ["A♠", "A♥"],
      boardCards: [],
      potSize: "$50",
      yourStack: "$500",
      action: "You're on the button. One player limps in front of you.",
      decision: "What should you do?",
      correctAnswer: "Raise to $15-20",
      explanation: "Pocket Aces are the strongest starting hand. You should raise to build the pot and narrow the field. Raising 3-4x the big blind is standard. This gives you the best chance to win a big pot with your premium hand.",
      playerAction: "Player 1 limps ($5)",
      concepts: ["Premium Hands", "Position Play", "Raise Sizing"]
    },
    {
      id: 2,
      title: "Top Pair - Value Betting",
      difficulty: "Beginner",
      position: "Small Blind",
      situation: "Flop",
      yourCards: ["K♦", "Q♦"],
      boardCards: ["K♣", "7♥", "2♠"],
      potSize: "$30",
      yourStack: "$450",
      action: "You raised pre-flop and got one caller. You flopped top pair with a Queen kicker.",
      decision: "What's your play?",
      correctAnswer: "Bet $20-25 (2/3 pot)",
      explanation: "You have top pair with a decent kicker. This is a value betting situation. Bet about 2/3 of the pot to extract value from worse hands like middle pair, and to charge draws to see another card. If you check here, you give free cards to dangerous hands.",
      playerAction: "Opponent checks to you",
      concepts: ["Value Betting", "Pot Control", "Hand Reading"]
    },
    {
      id: 3,
      title: "Flush Draw - Semi-Bluff Opportunity",
      difficulty: "Intermediate",
      position: "Cutoff",
      situation: "Turn",
      yourCards: ["A♦", "J♦"],
      boardCards: ["K♦", "9♦", "4♣", "2♥"],
      potSize: "$80",
      yourStack: "$400",
      action: "You have the nut flush draw. Opponent bets $40 on the turn.",
      decision: "Call or Raise?",
      correctAnswer: "Raise to $120-140",
      explanation: "With the nut flush draw (9 outs), you have approximately 36% equity to hit by the river. Raising here as a semi-bluff accomplishes two things: 1) You might win the pot immediately if opponent folds, 2) If called, you still have outs to make the best hand. This is more profitable than just calling.",
      playerAction: "Opponent bets $40",
      concepts: ["Semi-Bluffing", "Pot Odds", "Outs Calculation"]
    },
    {
      id: 4,
      title: "Set Mining - Calling with Small Pairs",
      difficulty: "Intermediate",
      position: "Big Blind",
      situation: "Pre-Flop",
      yourCards: ["5♣", "5♠"],
      boardCards: [],
      potSize: "$25",
      yourStack: "$500",
      action: "Button raises to $15. You have pocket fives in the big blind with $10 already invested.",
      decision: "Call, Fold, or Raise?",
      correctAnswer: "Call $10",
      explanation: "Small pocket pairs play best when you can see a cheap flop and try to hit a set (three of a kind). You're getting good pot odds to call ($25 to win $35), and if you flop a set, you'll often win a big pot. If you miss the flop (which happens ~88% of the time), you can easily fold to aggression.",
      playerAction: "Button raises to $15",
      concepts: ["Set Mining", "Implied Odds", "Pot Odds"]
    },
    {
      id: 5,
      title: "Continuation Bet - Following Through",
      difficulty: "Beginner",
      position: "Button",
      situation: "Flop",
      yourCards: ["A♠", "K♥"],
      boardCards: ["9♦", "7♣", "2♥"],
      potSize: "$40",
      yourStack: "$460",
      action: "You raised pre-flop and got called by the big blind. The flop completely missed you.",
      decision: "Check or Bet?",
      correctAnswer: "Bet $25-30 (C-Bet)",
      explanation: "This is a classic continuation bet spot. You showed strength pre-flop by raising, and this flop likely missed your opponent too. Betting here represents a strong hand and often wins the pot immediately. Even though you have nothing now, you have 6 outs to top pair and the initiative. C-betting is profitable here.",
      playerAction: "Opponent checks to you",
      concepts: ["C-Betting", "Initiative", "Fold Equity"]
    },
    {
      id: 6,
      title: "Slow Playing - Trapping with Quads",
      difficulty: "Advanced",
      position: "Under the Gun",
      situation: "Turn",
      yourCards: ["J♥", "J♦"],
      boardCards: ["J♠", "J♣", "K♦", "3♠"],
      potSize: "$60",
      yourStack: "$420",
      action: "You flopped quads (four Jacks)! Opponent has been betting aggressively.",
      decision: "Check or Bet?",
      correctAnswer: "Check (Slow Play)",
      explanation: "With an absolute monster (quads), you want to extract maximum value. Your opponent has been aggressive, so checking here allows them to continue bluffing or value betting worse hands. If you bet, they'll likely fold everything but the very best hands. Let them hang themselves by betting into your monster.",
      playerAction: "Opponent bets $40",
      concepts: ["Slow Playing", "Trapping", "Maximizing Value"]
    },
    {
      id: 7,
      title: "Folding Top Pair - Reading Danger",
      difficulty: "Advanced",
      position: "Big Blind",
      situation: "River",
      yourCards: ["A♣", "8♦"],
      boardCards: ["A♠", "J♥", "9♥", "7♥", "4♥"],
      potSize: "$200",
      yourStack: "$300",
      action: "You have top pair but the board shows four hearts and opponent shoves all-in for $300.",
      decision: "Call or Fold?",
      correctAnswer: "Fold",
      explanation: "Despite having top pair, the river completed a flush. When a tight player shoves this big on a scary board, they usually have what they represent. Calling here risks your entire stack when you're almost certainly beaten. Top pair is strong, but not strong enough to call an all-in bet on a four-flush board. Save your chips for better spots.",
      playerAction: "Opponent goes all-in $300",
      concepts: ["Hand Reading", "Board Texture", "Bankroll Management"]
    },
    {
      id: 8,
      title: "Stealing Blinds - Late Position Aggression",
      difficulty: "Intermediate",
      position: "Button",
      situation: "Pre-Flop",
      yourCards: ["K♠", "9♦"],
      boardCards: [],
      potSize: "$7.50",
      yourStack: "$500",
      action: "Everyone folds to you on the button. Blinds are tight players. You have K9 offsuit.",
      decision: "Raise or Fold?",
      correctAnswer: "Raise to $15",
      explanation: "This is a steal opportunity. K9o isn't a strong hand, but from the button against tight blinds, raising here is profitable. You're representing strength, and tight players will fold most of their range. Even if called, you have position throughout the hand. Stealing blinds adds up significantly over time.",
      playerAction: "Folds to you",
      concepts: ["Blind Stealing", "Position Advantage", "Range Advantage"]
    }
  ];

  const difficultyColors = {
    "Beginner": "bg-green-600/10 text-green-400 border-green-600/30",
    "Intermediate": "bg-yellow-600/10 text-yellow-400 border-yellow-600/30",
    "Advanced": "bg-red-600/10 text-red-400 border-red-600/30"
  };

  const currentHand = practiceHands[selectedHand];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-br from-primary/10 to-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/20 border border-primary/40 rounded-full mb-4">
              <Target className="w-4 h-4 text-primary" />
              <span className="text-primary text-sm font-bold uppercase tracking-wider">Practice Scenarios</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-heading font-bold mb-4">
              Learn by Example
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Master poker strategy with real hand scenarios and expert analysis.
            </p>
          </div>
        </div>
      </section>

      {/* Practice Area */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Hand Selector */}
            <div className="lg:col-span-1">
              <div className="bg-card border border-border rounded-xl p-6 sticky top-24">
                <h3 className="text-xl font-bold mb-4">Practice Hands</h3>
                <div className="space-y-2">
                  {practiceHands.map((hand, idx) => (
                    <button
                      key={hand.id}
                      onClick={() => setSelectedHand(idx)}
                      className={`w-full text-left p-4 rounded-lg border transition-all ${
                        selectedHand === idx
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:border-primary/50 hover:bg-card/50'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-sm">Hand #{hand.id}</span>
                        <span className={`text-xs px-2 py-1 rounded-full border ${difficultyColors[hand.difficulty]}`}>
                          {hand.difficulty}
                        </span>
                      </div>
                      <div className="text-sm text-muted-foreground line-clamp-2">{hand.title}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Hand Display */}
            <div className="lg:col-span-2">
              <div className="bg-card border border-border rounded-xl overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-primary/20 to-primary/5 p-6 border-b border-border">
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="text-2xl font-heading font-bold">{currentHand.title}</h2>
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold border ${difficultyColors[currentHand.difficulty]}`}>
                      {currentHand.difficulty}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <span>Position: <span className="text-primary font-semibold">{currentHand.position}</span></span>
                    <span>•</span>
                    <span>Situation: <span className="text-primary font-semibold">{currentHand.situation}</span></span>
                  </div>
                </div>

                {/* Table Visual */}
                <div className="p-8 bg-gradient-to-br from-green-950/30 to-background">
                  <div className="max-w-2xl mx-auto">
                    {/* Pot */}
                    <div className="text-center mb-6">
                      <div className="inline-block bg-card border-2 border-primary/40 rounded-xl px-6 py-3">
                        <div className="text-sm text-muted-foreground mb-1">Pot</div>
                        <div className="text-2xl font-bold text-primary">{currentHand.potSize}</div>
                      </div>
                    </div>

                    {/* Board Cards */}
                    {currentHand.boardCards.length > 0 && (
                      <div className="flex justify-center gap-2 mb-8">
                        {currentHand.boardCards.map((card, idx) => (
                          <div
                            key={idx}
                            className="bg-white text-black w-16 h-24 rounded-lg shadow-xl flex items-center justify-center text-3xl font-bold border-2 border-gray-200"
                          >
                            {card}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Your Cards */}
                    <div className="text-center mb-8">
                      <div className="text-sm text-muted-foreground mb-3 font-semibold">Your Cards</div>
                      <div className="flex justify-center gap-3">
                        {currentHand.yourCards.map((card, idx) => (
                          <div
                            key={idx}
                            className="bg-white text-black w-20 h-28 rounded-lg shadow-2xl flex items-center justify-center text-4xl font-bold border-4 border-primary/50"
                          >
                            {card}
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Stack Info */}
                    <div className="flex justify-center gap-6 text-sm">
                      <div className="text-center">
                        <div className="text-muted-foreground mb-1">Your Stack</div>
                        <div className="font-bold text-lg">{currentHand.yourStack}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Scenario Info */}
                <div className="p-6 space-y-6">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <AlertCircle className="w-5 h-5 text-primary" />
                      <h3 className="font-bold text-lg">The Situation</h3>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">{currentHand.action}</p>
                    {currentHand.playerAction && (
                      <div className="mt-2 p-3 bg-secondary/30 rounded-lg text-sm">
                        <span className="font-semibold">Action: </span>
                        <span className="text-muted-foreground">{currentHand.playerAction}</span>
                      </div>
                    )}
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="w-5 h-5 text-primary" />
                      <h3 className="font-bold text-lg">Your Decision</h3>
                    </div>
                    <p className="text-muted-foreground mb-3">{currentHand.decision}</p>
                    <div className="p-4 bg-green-600/10 border border-green-600/30 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-5 h-5 text-green-400" />
                        <span className="font-semibold text-green-400">Recommended Play</span>
                      </div>
                      <p className="text-lg font-bold mb-2">{currentHand.correctAnswer}</p>
                      <p className="text-sm text-muted-foreground leading-relaxed">{currentHand.explanation}</p>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Shuffle className="w-5 h-5 text-primary" />
                      <h3 className="font-bold text-lg">Key Concepts</h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {currentHand.concepts.map((concept, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-semibold border border-primary/30"
                        >
                          {concept}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Navigation */}
                <div className="p-6 bg-card/50 border-t border-border flex justify-between">
                  <button
                    onClick={() => setSelectedHand(Math.max(0, selectedHand - 1))}
                    disabled={selectedHand === 0}
                    className="px-6 py-3 bg-secondary text-secondary-foreground rounded-lg font-semibold hover:bg-secondary/80 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    Previous Hand
                  </button>
                  <button
                    onClick={() => setSelectedHand(Math.min(practiceHands.length - 1, selectedHand + 1))}
                    disabled={selectedHand === practiceHands.length - 1}
                    className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    Next Hand
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
